import { useEffect, useState } from "react";
import { cashPaymentService } from "../../services/cash-payment.service";

interface PendingReceipt {
  id: string;
  invoice_id: string | null;
  invoice_number: string | null;
  client_id: string;
  client_name: string;
  client_email: string;
  project_id: string | null;
  project_name: string | null;
  file_url: string;
  file_size: number;
  file_type: string | null;
  comment: string | null;
  amount_declared: number | null;
  method_declared: string | null;
  reference_declared: string | null;
  status: string;
  created_at: number;
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";
const ETL_URL = import.meta.env.VITE_API_URL_ETL || "http://localhost:8788";

export function ReceiptApprovalPage() {
  const [receipts, setReceipts] = useState<PendingReceipt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<PendingReceipt | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(`${API_URL}/api/internal/payments/receipts/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Error al cargar");
      setReceipts(json.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleApprove = async (receipt: PendingReceipt) => {
    if (!confirm(`¿Aprobar comprobante de ${receipt.client_name}?`)) return;
    setProcessingId(receipt.id);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(
        `${API_URL}/api/internal/payments/receipts/${receipt.id}/approve`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Error al aprobar");
      await load();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    setProcessingId(rejectModal.id);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(
        `${API_URL}/api/internal/payments/receipts/${rejectModal.id}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: rejectReason.trim() }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Error al rechazar");
      setRejectModal(null);
      setRejectReason("");
      await load();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(n);

  const formatDate = (t: number) =>
    new Date(t).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Comprobantes Pendientes</h1>
          <p className="text-gray-600 text-sm mt-1">
            {receipts.length} comprobante{receipts.length !== 1 ? "s" : ""} por revisar
          </p>
        </div>
        <button onClick={load} className="btn btn-secondary">
          🔄 Actualizar
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : receipts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <div className="text-5xl mb-3 opacity-40">✅</div>
          <p className="text-gray-600 font-medium">No hay comprobantes pendientes</p>
          <p className="text-sm text-gray-400 mt-1">
            Cuando los clientes suban comprobantes, aparecerán aquí.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {receipts.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-2xl border border-gray-200 p-5"
            >
              <div className="flex items-start gap-4 flex-wrap">
                {/* Preview */}
                <a
                  href={`${ETL_URL}/api/public/photos/receipts/${r.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center"
                >
                  {r.file_type?.startsWith("image/") ? (
                    <img
                      src={`${ETL_URL}/api/public/photos/receipts/${r.id}`}
                      alt="Comprobante"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">📄</span>
                  )}
                </a>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-semibold text-gray-900">{r.client_name}</h3>
                    <span className="text-xs text-gray-500">{r.client_email}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {r.invoice_number ? `Factura: ${r.invoice_number}` : "Sin factura específica"}
                    {r.project_name && ` • ${r.project_name}`}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm">
                    {r.amount_declared && (
                      <span className="font-bold text-primary">
                        {formatCurrency(r.amount_declared)}
                      </span>
                    )}
                    {r.method_declared && (
                      <span className="text-gray-600">
                        Método: {r.method_declared}
                      </span>
                    )}
                    {r.reference_declared && (
                      <span className="text-gray-600 text-xs">
                        Ref: {r.reference_declared}
                      </span>
                    )}
                  </div>
                  {r.comment && (
                    <p className="text-xs text-gray-500 mt-1 italic">
                      "{r.comment}"
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Subido: {formatDate(r.created_at)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleApprove(r)}
                    disabled={processingId === r.id}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition disabled:opacity-50"
                  >
                    {processingId === r.id ? "..." : "✅ Aprobar"}
                  </button>
                  <button
                    onClick={() => setRejectModal(r)}
                    disabled={processingId === r.id}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium text-sm transition disabled:opacity-50"
                  >
                    ❌ Rechazar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setRejectModal(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-3">Rechazar comprobante</h3>
            <p className="text-sm text-gray-600 mb-4">
              Cliente: <strong>{rejectModal.client_name}</strong>
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Explica por qué se rechaza el comprobante..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-primary outline-none resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectReason.trim() || processingId === rejectModal.id}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {processingId === rejectModal.id ? "..." : "Rechazar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}